/**
 * InflowModeStrategy
 * 
 * Strategy for the Wet Weather Stress (I&I) mode of the map.
 * Filters infrastructures by type (junction, storage, conduit) with Digital Twin,
 * and shows I&I metrics in the popup.
 */

import React from 'react';
import {
	MapModeStrategy,
	Infrastructure,
	PopupData,
	FetchParams,
	MapModeContext,
	DigitalTwinContext,
} from './MapModeStrategy';
import DashboardService from '@/domain/Dashboard/DashboardService';
import { DASHBOARD_MODE, InflowInfiltrationMetrics } from '@/domain/Dashboard/Dashboard';
import { formatDate } from '@/layout/DashboardLayout/utils';

// Lazy import of the InflowInfiltrationAssetPopup component
const InflowInfiltrationAssetPopup = React.lazy(
	() => import('@/components/Dashboard/InflowInfiltration/InflowInfiltrationAssetPopup')
);

// Service instance
const dashboardService = new DashboardService();

// Valid infrastructure types for I&I (case-insensitive)
const VALID_II_TYPES = ['junction', 'storage', 'conduit'];

// Extend PopupData to include I&I metrics (depth and flow)
interface InflowPopupData extends PopupData {
	depthMetrics?: InflowInfiltrationMetrics;
	flowMetrics?: InflowInfiltrationMetrics;
}

class InflowModeStrategy implements MapModeStrategy {
	name = 'inflow';
	displayName = 'Wet Weather Stress Lens';
	dragRotate = false;
	touchRotate = false;

	// Cache for metrics keyed by "assetId-modeType" (avoid re-fetch)
	private readonly metricsCache: Map<string, InflowInfiltrationMetrics> = new Map();

	/**
	 * In I&I mode, the backend already filters the infrastructures.
	 * Here we only filter by selected types and valid I&I infrastructure types.
	 */
	filterInfrastructures(
		infras: Infrastructure[],
		context: MapModeContext,
		selectedTypes: string[]
	): Infrastructure[] {
		if (!infras) return [];

		console.log('[InflowModeStrategy] filterInfrastructures input:', {
			totalInfras: infras.length,
			selectedTypes,
			infras: infras.map(i => ({
				id: i.measurementeInfraestructureId,
				name: i.measurementInfraestructureName,
				type: i.infrastructureTypeName,
				hasDigitalTwin: i.mainMeasurementTarget?.hasDigitalTwin,
			})),
		});

		const filtered = infras.filter((infra) => {
			const isSelectedType = selectedTypes.includes(infra.infrastructureTypeName);
			
			const isValidIIType = VALID_II_TYPES.some(
				(type) => infra.infrastructureTypeName?.toLowerCase() === type.toLowerCase()
			);

			const hasDigitalTwin = infra.mainMeasurementTarget?.hasDigitalTwin === true;

			if (!hasDigitalTwin) {
				console.log('[InflowModeStrategy] Asset filtered out (hasDigitalTwin is not true):', {
					id: infra.measurementeInfraestructureId,
					name: infra.measurementInfraestructureName,
					hasDigitalTwin: infra.mainMeasurementTarget?.hasDigitalTwin,
				});
			}

			return isSelectedType && isValidIIType && hasDigitalTwin;
		});

		console.log('[InflowModeStrategy] filterInfrastructures result:', filtered.length, 'assets');

		return filtered;
	}

	/**
	 * In I&I mode, all filtered icons have full opacity
	 */
	calculateOpacities(
		infras: Infrastructure[],
		context: MapModeContext,
		digitalTwinContext?: DigitalTwinContext
	): Record<number, number> {
		const opacities: Record<number, number> = {};
		
		if (!infras) return opacities;
		
		infras.forEach((infra) => {
			opacities[infra.measurementeInfraestructureId] = 1.0;
		});
		
		return opacities;
	}

	/**
	 * Fetch metrics for a specific mode type, using cache when available
	 */
	private async fetchMetrics(
		modeType: string,
		locationId: number,
		assetId: number,
		startDate: string,
		endDate: string
	): Promise<InflowInfiltrationMetrics | undefined> {
		const cacheKey = `${assetId}-${modeType}`;
		const cached = this.metricsCache.get(cacheKey);
		if (cached) return cached;

		const metrics = await dashboardService.getInflowInfiltrationMetrics(
			modeType,
			locationId,
			assetId,
			startDate,
			endDate
		);

		if (metrics) {
			this.metricsCache.set(cacheKey, metrics);
		}
		return metrics;
	}

	/**
	 * In I&I mode, get I&I metrics (depth and flow) from the backend
	 */
	async handleAssetClick(
		infra: Infrastructure,
		context: MapModeContext
	): Promise<InflowPopupData> {
		const assetId = infra.measurementeInfraestructureId;
		const locationId = context.selectedLocation as unknown as number;
		const startDate = context.selectedDate?.[0] ? formatDate(context.selectedDate[0]) : '';
		const endDate = context.selectedDate?.[1] ? formatDate(context.selectedDate[1]) : '';

		try {
			// Fetch both depth and flow metrics in parallel
			const [depthMetrics, flowMetrics] = await Promise.all([
				this.fetchMetrics(DASHBOARD_MODE.INFILTRATION_DEPTH, locationId, assetId, startDate, endDate),
				this.fetchMetrics(DASHBOARD_MODE.INFILTRATION_FLOW, locationId, assetId, startDate, endDate),
			]);

			return {
				measurementeInfraestructureId: assetId,
				name: infra.measurementInfraestructureName,
				measurementInfraestructureName: infra.measurementInfraestructureName,
				infrastructureTypeName: infra.infrastructureTypeName,
				expired: infra.mainMeasurementTarget?.expired,
				depthMetrics,
				flowMetrics,
			};
		} catch (error) {
			console.error('Error fetching I&I metrics:', error);

			return {
				measurementeInfraestructureId: assetId,
				name: infra.measurementInfraestructureName,
				measurementInfraestructureName: infra.measurementInfraestructureName,
				infrastructureTypeName: infra.infrastructureTypeName,
				expired: infra.mainMeasurementTarget?.expired,
			};
		}
	}

	/**
	 * Popup component for I&I mode
	 */
	PopupComponent = InflowInfiltrationAssetPopup;

	/**
	 * In I&I mode, send the mode parameter to filter in backend
	 */
	getFetchParams(context: MapModeContext): FetchParams {
		return {
			mode: DASHBOARD_MODE.INFILTRATION_FLOW,
		};
	}

	/**
	 * In I&I mode, there are no specific highlighted assets
	 */
	getHighlightedAssets(
		context: MapModeContext,
		digitalTwinContext?: DigitalTwinContext
	): Set<number> | null {
		return null;
	}

	/**
	 * Props for the popup in I&I mode
	 */
	getPopupProps(
		infraInfo: InflowPopupData,
		context: MapModeContext,
		additionalData?: any
	): Record<string, any> {
		return {
			assetId: infraInfo.measurementeInfraestructureId,
			assetName: infraInfo.name || infraInfo.measurementInfraestructureName,
			assetType: infraInfo.infrastructureTypeName || 'Unknown',
			depthMetrics: infraInfo.depthMetrics,
			flowMetrics: infraInfo.flowMetrics,
		};
	}

	/**
	 * Clear metrics cache (useful when location changes)
	 */
	clearCache(): void {
		this.metricsCache.clear();
	}
}

// Export singleton instance
export const inflowModeStrategy = new InflowModeStrategy();
export default InflowModeStrategy;
